/**
 * The 17-stop guided tour, as data.
 *
 * Source: `const TOUR=[ … ]` at line 3844 of
 * `/Users/timwark/Projects/Data/OEM+/OEM+_Platform_Demo.html`, described as
 * "about twelve minutes" in Help → Guided tour and in §1.8 of the How-To Guide.
 *
 * Porting decisions
 * - Each demo stop is a tuple `[title, body, go]`. `go` was a handler string
 *   that `tourGo()` passed to `eval`. Every one of the 17 was a bare
 *   show(view[, sub]) call, so each is carried here as a typed `OemAction`
 *   built with `act()`. The tour engine resolves it through `routeForView`, so
 *   a change to the route table moves the tour with it.
 * - Titles and bodies are verbatim. None of them names the OEM, and none says
 *   "mock", so the copy needed no reframing.
 * - `anchor` is new. The demo only navigated. The port also scrolls to the
 *   card a stop is talking about when two stops share a page (stops 2 and 3 are
 *   both Asset360). It is the tail of a `data-comment-anchor` path, so it names
 *   an anchor that `comments/anchors.ts` already registers.
 * - `ask` is new too. Stop 17 tells the presenter to ask the assistant a
 *   question, and the tour bar offers that question as a one-click button.
 * - `persona` is supported but no stop sets it, because the demo tour never
 *   switched persona.
 *
 * Route mapping (the demo view and sub → the port's route today):
 *   01 section home        → /
 *   02 asset360            → /
 *   03 asset360            → /          (anchor: IssueSummary)
 *   04 alerts AL-0923      → /issues/alerts/AL-0923
 *   05 issues              → /issues
 *   06 section workflows   → /workflows
 *   07 module diagnose     → /workflows/modules/diagnose
 *   08 wf assess           → /workflows/steps/assess
 *   09 wf ready            → /workflows/steps/ready
 *   10 jobrun              → /workflows/case-trace
 *   11 incident IR-2026-015→ /issues/incidents/IR-2026-015
 *   12 loops learning      → /loops/learning
 *   13 overview            → /sites
 *   14 sites MR            → /sites/MR
 *   15 machine L-33        → /machines/L-33
 *   16 data                → /integration/data
 *   17 asset360            → /          (ask: the cost-of-waiting question)
 *
 * Data Schema layer — pure data, no behaviour.
 */

import type { OemPersonaId } from '../OemPersonaContext';
import { act, type OemAction } from './action';

/** One stop on the guided tour. */
export interface TourStop {
  /** Stable key, used for React keys and the comment anchors on the tour list. */
  readonly id: string;
  /** The demo's show() call for this stop, resolved to a route by `routeForView`. */
  readonly action: OemAction;
  /** Short imperative title, e.g. "Open a machine". */
  readonly title: string;
  /** One or two sentences the tour bar carries as commentary. */
  readonly body: string;
  /** Switch to this persona, without the persona's own navigation, before the stop. */
  readonly persona?: OemPersonaId;
  /**
   * Tail of a `data-comment-anchor` path to scroll into view once the page has
   * rendered, e.g. `'Asset360Scene.IssueSummary'`. If nothing matches (a
   * collapsed band, a scene that renamed its anchor), the stop still works; it
   * just does not scroll.
   */
  readonly anchor?: string;
  /** A question the tour bar offers to send to the assistant. */
  readonly ask?: string;
}

/** The demo's own figure, used in the Help tab's subtitle. */
export const TOUR_MINUTES = 'about twelve minutes';

/** The 17 stops, in order. */
export const TOUR: readonly TourStop[] = [
  {
    id: 'home',
    action: act('section', 'home'),
    title: 'Start on Home',
    body: 'The section page shows what the whole network is doing this morning — machines, open jobs, alerts and availability — with a tile for everything underneath it.',
  },
  {
    id: 'asset360',
    action: act('asset360'),
    title: 'Open Asset360',
    body: 'The control tower first: eight measures, each coloured on track, watch or needs action. This is the aftermarket P&L on one row.',
    anchor: 'Asset360Scene.ControlTower',
  },
  {
    id: 'issues-summary',
    action: act('asset360'),
    title: 'Read the issues summary',
    body: 'Directly under the tower: four counters and the alerts that need a decision this morning. Every row opens that alert.',
    anchor: 'Asset360Scene.IssueSummary',
  },
  {
    id: 'alert',
    action: act('alerts', 'AL-0923'),
    title: 'Follow one alert',
    body: 'The window counting down, the evidence the agents pulled, the model confidence and the drafted action — nothing has touched a machine yet.',
  },
  {
    id: 'live',
    action: act('issues'),
    title: 'See what it costs to leave it',
    body: 'Live prices the planned job against the same failure allowed to happen, using the site rate card.',
  },
  {
    id: 'workflows',
    action: act('section', 'workflows'),
    title: 'Open the Workflows map',
    body: 'Ten modules in one view with what is open in each. The model, not a menu.',
  },
  {
    id: 'module',
    action: act('module', 'diagnose'),
    title: 'Go into a module',
    body: '04 Assess & Prescribe — what it covers, what is open, which agents run it and which systems it reads, with its steps as tiles.',
  },
  {
    id: 'step',
    action: act('wf', 'assess'),
    title: 'Open a step',
    body: 'Diagnosis & prescription: every open assessment as signal → finding → prescription, with the confidence and the evidence behind each.',
  },
  {
    id: 'promise',
    action: act('wf', 'ready'),
    title: 'Check the promise',
    body: '06 Service Planning → Promise & readiness: parts, people, roster and access scored on every planned job before a date is offered.',
  },
  {
    id: 'case-trace',
    action: act('jobrun'),
    title: 'Trace a case end to end',
    body: 'One case through the phases with the task board underneath — the audit trail a customer can be walked through.',
  },
  {
    id: 'incident',
    action: act('incident', 'IR-2026-015'),
    title: 'Replay an incident',
    body: 'IR-2026-015, the L-18 engine-bay fire: what happened, then the same fortnight replayed through the agent chain on data that already existed.',
  },
  {
    id: 'learning',
    action: act('loops', 'learning'),
    title: 'See what the fleet learned',
    body: 'Fleet learning: the rules now running across every machine of that type, each traced to the single job it came from.',
  },
  {
    id: 'network',
    action: act('overview'),
    title: 'Look at the network',
    body: 'The sites map: six mines, pins coloured by the worst machine on site. Drag a mine name to move it.',
  },
  {
    id: 'site',
    action: act('sites', 'MR'),
    title: 'Open a site',
    body: 'Mount Read Metals: an all-battery loader fleet, a three-day freight lag across Bass Strait, one HV-authorised technician.',
  },
  {
    id: 'machine',
    action: act('machine', 'L-33'),
    title: 'Open a machine',
    body: 'L-33: the 360° view, the condition register, the history, the cost and its Maximo service record.',
  },
  {
    id: 'data',
    action: act('data'),
    title: 'See what it is built on',
    body: 'Integration → Data: every file in the store in the format its system produces.',
  },
  {
    id: 'assistant',
    action: act('asset360'),
    title: 'Ask the assistant',
    body: '“What does doing nothing cost us today?” — it answers from the same tables and shows the queries it ran.',
    ask: 'What does doing nothing cost us today?',
  },
];
